/**
 * 판이 끝난 뒤 저장(FS-04-03 '리플레이 저장 상태' · SR-REC-01 · SR-LCL-03 왕복 ≤2초):
 *   exportReplay() → POST …/replays(meta · file · track) → POST …/attempts {…, replayId}
 * 상태: saving → saved | offline(이 기기에 보관 — 연결되면 자동으로 다시) | failed(다시 저장 버튼) | tooBig(413 — 기록만 저장)
 *       | none(기록 없음 — 호스트가 리플레이를 주지 않음. 도전 기록만 올린다)
 * 오프라인 보관함(outbox): sessionStorage(없으면 메모리) — 탭을 닫으면 사라진다. 올리면 지운다.
 * 보관함의 주행은 고스트 후보('나의 지난 주행 n')로도 쓴다({@link outboxGhostSource}) — S3 의 이 기기 고스트를 대신한다.
 */
import type { ReplayExport, ReplayMeta, RunBundle } from "@picnic-field/local-host/protocol";
import { toApiError } from "../../../lib/http/errors";
import type { AttemptInput, SocialApi } from "../../../lib/api/socialApi";
import { isTooLarge } from "../../../lib/api/socialApi";
import type { SaveResult, GhostCandidate, GhostSource } from "../types";

interface Pending {
  id: string;
  at: number;
  /** 아직 못 올린 리플레이(이미 올렸으면 null) */
  replay: { meta: ReplayMeta; file: string; track: string } | null;
  /** 이미 올린 리플레이 id(도전 기록만 남았을 때) */
  replayId: string | null;
  attempt: AttemptInput;
  /** 고스트로 쓸 때 그릴 모형 */
  bundle: RunBundle | null;
  label?: string;
}

type Uploader = Pick<SocialApi, "uploadReplay" | "postAttempt">;

const STORE = "pf.replay.outbox";
const MAX = 5;
const memory: Pending[] = [];

function toB64(a: Uint8Array): string {
  let s = "";
  for (let i = 0; i < a.length; i += 0x8000) s += String.fromCharCode(...a.subarray(i, i + 0x8000));
  return btoa(s);
}

function fromB64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function readOutbox(): Pending[] {
  try {
    const raw = window.sessionStorage.getItem(STORE);
    if (raw) return JSON.parse(raw) as Pending[];
  } catch {
    /* 저장소 없음 · 깨짐 → 메모리 */
  }
  return [...memory];
}

function writeOutbox(xs: Pending[]) {
  const keep = xs.sort((a, b) => b.at - a.at).slice(0, MAX);
  memory.splice(0, memory.length, ...keep);
  try {
    window.sessionStorage.setItem(STORE, JSON.stringify(keep));
  } catch {
    /* 용량 초과 — 메모리에는 남는다 */
  }
}

export function clearOutbox() {
  writeOutbox([]);
}

let serial = 0;

function keep(p: Pending) {
  writeOutbox([...readOutbox().filter((x) => x.id !== p.id), p]);
}

function drop(id: string) {
  writeOutbox(readOutbox().filter((x) => x.id !== id));
}

const offline = (e: unknown) => toApiError(e).code === "NET-OFFLINE";

async function upload(api: Uploader, p: Pending): Promise<SaveResult> {
  let replayId = p.replayId;
  if (p.replay) {
    try {
      const r = await api.uploadReplay({
        meta: p.replay.meta,
        file: fromB64(p.replay.file),
        positionTrack: fromB64(p.replay.track),
      });
      replayId = r.replayId;
    } catch (e) {
      if (isTooLarge(e)) {
        // 다시 올려도 같다 — 리플레이는 버리고 도전 기록만
        const rest = await postOnly(api, { ...p, replay: null, replayId: null });
        return { ...rest, state: rest.state === "none" ? "tooBig" : rest.state };
      }
      keep(p);
      return { state: offline(e) ? "offline" : "failed", replayId: null, pendingId: p.id };
    }
  }
  return postOnly(api, { ...p, replay: null, replayId });
}

async function postOnly(api: Uploader, p: Pending): Promise<SaveResult> {
  try {
    const posted = await api.postAttempt({ ...p.attempt, replayId: p.replayId ?? undefined });
    drop(p.id);
    return {
      state: p.replayId ? "saved" : "none",
      replayId: p.replayId,
      pendingId: null,
      attemptId: posted?.attemptId ?? null,
      verification: posted?.verification ?? null,
    };
  } catch (e) {
    // 리플레이는 올라갔고 도전 기록만 남았다 — 보관함에 남겨 다음에 보낸다(학생에게는 저장됨)
    keep({ ...p, replay: null });
    if (p.replayId) return { state: "saved", replayId: p.replayId, pendingId: null };
    return { state: offline(e) ? "offline" : "failed", replayId: null, pendingId: p.id };
  }
}

/**
 * 한 판 저장. exp 가 null 이면(호스트가 기록을 주지 않음) 도전 기록만.
 * 실패해도 던지지 않는다 — 결과 화면 상태로 돌려준다.
 */
export async function saveRun(
  api: Uploader,
  exp: ReplayExport | null,
  attempt: AttemptInput,
  bundle: RunBundle | null,
): Promise<SaveResult> {
  const p: Pending = {
    id: `pending-${Date.now().toString(36)}-${++serial}`,
    at: Date.now(),
    replay: exp ? { meta: exp.meta, file: toB64(exp.file), track: toB64(exp.positionTrack) } : null,
    replayId: null,
    attempt,
    bundle,
  };
  return upload(api, p);
}

/** 보관함 항목 하나 다시(결과 화면 '다시 저장') */
export async function retryPending(api: Uploader, id: string): Promise<SaveResult | null> {
  const p = readOutbox().find((x) => x.id === id);
  return p ? upload(api, p) : null;
}

/** 보관함 전부 다시(화면 진입 · 'online' 이벤트). 올린 수 */
export async function flushOutbox(api: Uploader): Promise<number> {
  let n = 0;
  for (const p of readOutbox()) {
    const r = await upload(api, p);
    if (r.state === "saved" || r.state === "none" || r.state === "tooBig") n += 1;
    else if (r.state === "offline") break;
  }
  return n;
}

/** 아직 못 올린 내 주행 → 고스트 후보(위치 트랙 없이는 못 그리므로 리플레이가 있는 것만) */
export function outboxGhostSource(label: (n: number) => string): GhostSource {
  return {
    async list(presetKey): Promise<GhostCandidate[]> {
      return readOutbox()
        .filter((p) => p.replay && p.bundle && p.attempt.presetKey === presetKey)
        .map((p, i) => ({
          ghostId: p.id,
          label: label(i + 1),
          completed: p.attempt.outcome === "complete",
          source: "local" as const,
          load: async () => ({
            kind: "replay" as const,
            replay: {
              ghostId: p.id,
              label: label(i + 1),
              completed: p.attempt.outcome === "complete",
              bundle: p.bundle as RunBundle,
              file: fromB64(p.replay!.file),
              positionTrack: fromB64(p.replay!.track),
            },
          }),
        }));
    },
  };
}
